import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Type definitions for the e09 status data
export interface QuarterData {
  ppo: string;
  date: string;
  version: number | string;
  status: string;
}

export interface E09StatusItem {
  ppo: string;
  year: string | number;
  version?: string | number;
  status?: string;
  closed?: boolean;
  quarters: {
    q1?: QuarterData;
    q2?: QuarterData;
    q3?: QuarterData;
    q4?: QuarterData;
  };
}

export interface E09StatusResponse {
  inbound: E09StatusItem[];
  outbound: E09StatusItem[];
}

// Parameters for filtering the status data
export interface E09StatusParams {
  year?: string;
  ppo?: string;
  inbound?: boolean;
  open?: boolean;
}

// Query key for consistent reference
export const e09StatusQueryKey = ['e09-status'];

/**
 * Fetches e09 status data from the API
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Promise with the e09 status data
 */
const fetchE09Status = async (params?: E09StatusParams): Promise<E09StatusResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.year) {
      queryParams.append('year', params.year);
    }
    
    if (params?.ppo) {
      queryParams.append('ppo', params.ppo);
    }
    
    if (params?.inbound !== undefined) {
      queryParams.append('inbound', params.inbound.toString());
    }
    
    if (params?.open !== undefined) {
      queryParams.append('open', params.open.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/data-set/e09/status${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching e09 status data:', error);
    throw error;
  }
};

/**
 * Hook for fetching and managing e09 status data
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Query result with e09 status data, loading state, error state, and refetch function
 */
export function useE09Status(params?: E09StatusParams) {
  return useQuery<E09StatusResponse>({
    queryKey: [...e09StatusQueryKey, params],
    queryFn: () => fetchE09Status(params),
    retry: 2,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });
} 